import * as _ from "lodash";

import { ChartSeriesType, DataDescriptorEntity, LineData } from "@ctra/api";

import { LineChartProcessor } from "./Core";
import { ChartContextType } from "../../../../providers";
import { i18nTranslate, Enterprise as Content } from "@ctra/i18n";

const {
  kpi: { displayName }
} = Content;

export class MultiEntityLineChartProcessor extends LineChartProcessor {
  public identity = "MultiEntityLineChartProcessor";

  /**
   * Check if the legend is visible
   * @param {ChartContextType<LineData>["values"]["meta"]} meta
   * @param {ChartContextType<LineData>["values"]["series"]} series
   * @param {Record<string, DataDescriptorEntity>} variants
   * @returns {boolean}
   */
  isLegendVisible(
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"],
    variants: Record<string, DataDescriptorEntity>
  ): boolean {
    return super.isLegendVisible(meta, series, variants);
  }

  /**
   * Make a new series object with unique keys
   * @param {ChartContextType<LineData>["values"]["meta"]} meta
   * @param {ChartContextType<LineData>["values"]["series"]} series
   * @param {Record<string, DataDescriptorEntity>} variants
   * @returns {{[p: string]: string} | {[p: string]: {}}}
   */
  getSeries(
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"],
    variants: Record<string, DataDescriptorEntity>
  ) {
    /**
     * Tell if the overlay is on a single farm
     * @type {boolean}
     */
    const isSameFarm = _.size(_.uniq(_.compact(_.flatMap(meta, "farmIds")))) === 1;

    /**
     * Collect all series and make them unique per chart
     * @type {{[p: string]: {}}}
     */
    return _.reduce(
      _.mapValues(meta, "series.keys"),
      (acc, series, chartReference) => {
        const [chartID] = _.split(chartReference, "-");
        const updatedKey = _.mapKeys(series, (v, key) => _.join([chartID, key], "."));
        const isFarmSeries = _.get(meta, [chartReference, "seriesType"]) === ChartSeriesType.farm;
        const title = _.get(variants, [chartReference, "dataProperties", "typeName"]);

        if (isFarmSeries) {
          return isSameFarm
            ? // same farm - don't add farm name to metric name
              {
                ...acc,
                ..._.mapValues(updatedKey, (v) => {
                  const metric = title
                    ? i18nTranslate(displayName(title), {
                        case: null,
                        variant: null,
                        makeDefaultValue: true
                      })
                    : null;

                  return metric || v;
                })
              }
            : // different farms - add farm name to metric name
              {
                ...acc,
                ..._.mapValues(updatedKey, (v) => {
                  const metric = title
                    ? i18nTranslate(displayName(title), {
                        case: null,
                        variant: null,
                        makeDefaultValue: true
                      })
                    : null;

                  return metric ? `${v} - ${metric}` : v;
                })
              };
        } else {
          return isSameFarm
            ? // same farm - add metric name to series title
              {
                ...acc,
                ..._.mapValues(updatedKey, (v) => {
                  const metric = title
                    ? i18nTranslate(displayName(title), {
                        case: null,
                        variant: null,
                        makeDefaultValue: true
                      })
                    : null;

                  return metric ? `${v} - ${metric}` : v;
                })
              }
            : // different farms - don't add metric name to series title
              {
                ...acc,
                ...updatedKey
              };
        }
      },
      {}
    );
  }

  /**
   * Update the data so the series field is unique for each line
   * @param {ChartContextType<LineData>["data"]} data
   * @param {ChartContextType<LineData>["meta"]} meta
   * @param {ChartContextType<LineData>["series"]} series
   * @returns {ChartContextType<LineData>["data"]}
   */
  getConsolidatedData(
    data: ChartContextType<LineData>["values"]["data"],
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"]
  ): ChartContextType<LineData>["values"]["data"] {
    return _.mapValues(this.getFilteredRawData(data, meta, series), (pointCollection, chartReference) => {
      const [chartID] = _.split(chartReference, "-");

      return _.map(pointCollection, (point) => ({
        ...point,
        seriesField: [chartID, point.seriesField].join(".")
      }));
    });
  }
}
